import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {IntermediaryEntity} from '../services/api.bokkz.services';
import {BokkzAlgDataService} from '../services/bokkz-alg-data-service';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {filter, Observable, shareReplay, take} from 'rxjs';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TemplateEntity, TemplateOptions, TemplateService} from "../services/template.api";
import {ToastService} from "@snelstart/ui-toast";

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-template',
  styleUrls: ['./template.component.css'],
  standalone: true,
  templateUrl: './template.component.html',
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiTabModule,
    SharedUiAlertModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class TemplateComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public dialogVisible = false;
  @Input() public sstRelationId: any;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();
  private readonly bokkzClient = inject(TemplateService);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  readonly intermediaryEntity$: Observable<IntermediaryEntity> = this.algDataService.intermediaryEntity$.pipe(
    filter((data): data is IntermediaryEntity => !!data?.sstPrimaryAdminId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public formGroup = new FormGroup({
    type: new FormControl('office'),
    marginTop: new FormControl<number | null>(25, [Validators.min(0), Validators.max(40)]),
    marginRight: new FormControl<number | null>(30, [Validators.min(0), Validators.max(40)]),
    marginBottom: new FormControl<number | null>(25, [Validators.min(0), Validators.max(40)]),
    marginLeft: new FormControl<number | null>(30, [Validators.min(0), Validators.max(40)]),
    officeLogoPosition: new FormControl<TemplateOptions | null>(TemplateOptions.left),
    logoFirstPage: new FormControl<File | null>(null),
    footerType: new FormControl<string | null>('text'),
    footerImage: new FormControl<File | null>(null),
    footerText: new FormControl<string | null>(null),
    font: new FormControl<string | null>(null),
  });

  private readonly MAX_IMAGE_BYTES = 2 * 1024 * 1024;
  private readonly MAX_IMAGE_WIDTH = 4000;
  private readonly MAX_IMAGE_HEIGHT = 4000;
  private readonly MAX_TOTAL_PIXELS = 8_000_000;
  private readonly PDF_A4_WIDTH = 595.44;
  private readonly PDF_A4_HEIGHT = 842.4;
  private readonly PREVIEW_A4_WIDTH = 160;
  private readonly PREVIEW_A4_HEIGHT = 226;
  private readonly IMAGE_DPI = 150;
  private readonly PDF_POINTS_PER_INCH = 72;
  private readonly HEADER_MAX_HEIGHT = 90;
  private readonly HEADER_MAX_WIDTH = 180;
  private readonly FOOTER_HORIZONTAL_MARGIN = 50;
  private readonly FOOTER_MAX_HEIGHT = 80;
  public headerPreviewStyle: Record<string, string> | null = null;
  public footerPreviewStyle: Record<string, string> | null = null;
  public footerTextStyle: Record<string, string> | null = null;
  public headerPreviewUrl: string | null = null;
  public footerPreviewUrl: string | null = null;

  public footerTypeOptions = [
    {label: 'Afbeelding', value: 'image'},
    {label: 'Tekst', value: 'text'},
  ];
  public logoPositionOptions = [
    {label: 'Links', value: TemplateOptions.left},
    {label: 'Rechts', value: TemplateOptions.right},
  ];

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: true,
  });

  ngOnInit(): void {
    this.intermediaryEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    if (this.formGroup !== undefined) {
      this.formGroup.valueChanges.subscribe(async () => {
        const headerFile = this.formGroup.get('logoFirstPage')?.value;
        const footerFile = this.formGroup.get('footerImage')?.value;
        if (headerFile) {
          await this.buildHeaderPreviewStyle(headerFile);
        }
        if (footerFile) {
          await this.buildFooterPreviewStyle(footerFile);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dialogVisible']?.currentValue === true) {
      this.bokkzClient.getTemplate('office').pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((data) => {
        this.formGroup.patchValue({
          type: data.type ?? 'office',
          marginTop: data.marginTop ?? 25,
          marginRight: data.marginRight ?? 30,
          marginBottom: data.marginBottom ?? 25,
          marginLeft: data.marginLeft ?? 30,
          officeLogoPosition: data.officeLogoPosition ?? TemplateOptions.left,
          footerType: data.footerType ?? 'text',
          footerText: data.footerText ?? null,
        }, {emitEvent: false});

        const logoFirstPage = this.byteArrayToFile(data.logoFirstPage, 'header-logo');
        this.formGroup.patchValue({logoFirstPage}, {emitEvent: false});
        if (this.headerPreviewUrl) {
          URL.revokeObjectURL(this.headerPreviewUrl);
        }
        this.headerPreviewUrl = logoFirstPage ? URL.createObjectURL(logoFirstPage) : null;
        if (logoFirstPage) {
          void this.buildHeaderPreviewStyle(logoFirstPage);
        }

        const footerImage = this.byteArrayToFile(data.footerImage, 'footer-logo');
        this.formGroup.patchValue({footerImage}, {emitEvent: false});
        if (this.footerPreviewUrl) {
          URL.revokeObjectURL(this.footerPreviewUrl);
        }
        this.footerPreviewUrl = footerImage ? URL.createObjectURL(footerImage) : null;
        if (footerImage) {
          void this.buildFooterPreviewStyle(footerImage);
        }
      });
    }
  }


  ngOnDestroy(): void {
    if (this.headerPreviewUrl) {
      URL.revokeObjectURL(this.headerPreviewUrl);
    }
    if (this.footerPreviewUrl) {
      URL.revokeObjectURL(this.footerPreviewUrl);
    }
  }

  public async saveTemplate(): Promise<void> {
    const formValue = this.formGroup.getRawValue();
    const logoFirstPage = await this.fileToByteArray(formValue.logoFirstPage);
    const footerImage = await this.fileToByteArray(formValue.footerImage);
    const template: TemplateEntity = {
      type: formValue.type ?? undefined,
      marginTop: formValue.marginTop ?? undefined,
      marginRight: formValue.marginRight ?? undefined,
      marginBottom: formValue.marginBottom ?? undefined,
      marginLeft: formValue.marginLeft ?? undefined,
      officeLogoPosition: formValue.officeLogoPosition ?? undefined,
      logoFirstPage,
      footerType: formValue.footerType ?? undefined,
      footerImage,
      footerText: formValue.footerText ?? undefined,
      font: formValue.font ?? undefined,
    };
    this.bokkzClient.postTemplate(template)
      .subscribe({
        next: () => {
          this.toastService.showSuccessMessage("Briefopmaak succesvol ingesteld.")
        },
        error: () => {
          this.toastService.showErrorMessage("Briefopmaak niet succesvol ingesteld. Probeer het nogmaals of neem contact op met de klantenservice.")
        }
      });
  }

  private byteArrayToFile(payload?: number[] | string | { data: number[] }, fileName = 'template-image'): File | null {
    const bytes = this.extractBytes(payload);
    if (!bytes.length) {
      return null;
    }

    const mimeType = this.detectImageMimeType(bytes);
    const extension = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/gif' ? 'gif' : 'png';
    return new File([new Uint8Array(bytes)], `${fileName}.${extension}`, {type: mimeType});
  }

  private async fileToByteArray(file: File | null): Promise<number[] | undefined> {
    if (!file) {
      return undefined;
    }
    const buffer = await file.arrayBuffer();
    return Array.from(new Uint8Array(buffer));
  }

  private extractBytes(payload?: number[] | string | { data: number[] }): number[] {
    if (!payload) {
      return [];
    }

    if (Array.isArray(payload)) {
      return payload.map((value) => (value < 0 ? value + 256 : value));
    }

    if (typeof payload === 'string') {
      const base64 = payload.includes(',') ? payload.split(',')[1] : payload;
      const binary = atob(base64);
      return Array.from(binary, (char) => char.charCodeAt(0));
    }

    if (Array.isArray(payload.data)) {
      return payload.data.map((value) => (value < 0 ? value + 256 : value));
    }

    return [];
  }

  private detectImageMimeType(bytes: number[]): string {
    if (bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47) {
      return 'image/png';
    }

    if (bytes.length >= 3 &&
      bytes[0] === 0xff &&
      bytes[1] === 0xd8 &&
      bytes[2] === 0xff) {
      return 'image/jpeg';
    }

    if (bytes.length >= 4 &&
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38) {
      return 'image/gif';
    }

    return 'image/png';
  }

  private pixelsToPoints(pixels: number):
    number {
    return (pixels / this.IMAGE_DPI) * this.PDF_POINTS_PER_INCH;
  }

  private scaleToFitBox(naturalWidth: number, naturalHeight: number, maxWidth: number, maxHeight: number):
    {
      width: number;
      height: number
    } {
    if (!naturalWidth || !naturalHeight) {
      return {width: 0, height: 0};
    }
    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
    return {
      width: naturalWidth * scale,
      height: naturalHeight * scale,
    };
  }

  public toPreviewX(valueInPdfPoints: number):
    number {
    return (valueInPdfPoints / this.PDF_A4_WIDTH) * this.PREVIEW_A4_WIDTH;
  }

  public toPreviewY(valueInPdfPoints: number):
    number {
    return (valueInPdfPoints / this.PDF_A4_HEIGHT) * this.PREVIEW_A4_HEIGHT;
  }

  private async buildHeaderPreviewStyle(file: File):
    Promise<void> {
    const size = await this.getImageSize(file);

    const naturalWidth = this.pixelsToPoints(size.width);
    const naturalHeight = this.pixelsToPoints(size.height);

    const headerSize = this.scaleToFitBox(
      naturalWidth,
      naturalHeight,
      this.HEADER_MAX_WIDTH,
      this.HEADER_MAX_HEIGHT
    );

    const topMargin = +(this.formGroup.get('marginTop')?.value || 25);
    const leftMargin = +(this.formGroup.get('marginLeft')?.value || 30);
    const rightMargin = +(this.formGroup.get('marginRight')?.value || 30);
    const position = this.formGroup.get('officeLogoPosition')?.value || TemplateOptions.left;

    const style: Record<string, string> = {
      top: `${this.toPreviewY(topMargin)}px`,
      width: `${this.toPreviewX(headerSize.width)}px`,
      height: `${this.toPreviewY(headerSize.height)}px`,
    };

    if (position === TemplateOptions.right) {
      style['right'] = `${this.toPreviewX(rightMargin)}px`;
      style['left'] = 'auto';
    } else {
      style['left'] = `${this.toPreviewX(leftMargin)}px`;
      style['right'] = 'auto';
    }

    this.headerPreviewStyle = style;
  }

  private async buildFooterPreviewStyle(file: File): Promise<void> {
    const size = await this.getImageSize(file);

    const footerPlacement = this.scaleToFitPageWidthWithOptionalMargin(
      size.width,
      size.height,
      this.PDF_A4_WIDTH
    );

    const rawWidth = this.toPreviewX(footerPlacement.width);
    const rawHeight = this.toPreviewY(footerPlacement.height);

    const maxFooterHeight = 18;
    const heightScale = rawHeight > maxFooterHeight ? maxFooterHeight / rawHeight : 1;

    const footerPreviewScale = 0.8;

    const finalWidth = rawWidth * heightScale * footerPreviewScale;
    const finalHeight = rawHeight * heightScale * footerPreviewScale;

    const bottomMargin = +(this.formGroup.get('marginBottom')?.value || 0);

    this.footerTextStyle = {
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: '8px',
    };

    this.footerPreviewStyle = {
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${finalWidth}px`,
      height: `${finalHeight}px`,
      bottom: `${this.toPreviewY(bottomMargin)}px`,
    };
  }

  private scaleToFitPageWidthWithOptionalMargin(originalWidth: number, originalHeight: number, pageWidth: number):
    {
      x: number;
      width: number;
      height: number
    } {
    const fullWidthScale = pageWidth / originalWidth;
    const fullWidthHeight = originalHeight * fullWidthScale;

    if (fullWidthHeight <= this.FOOTER_MAX_HEIGHT) {
      return {
        x: 0,
        width: pageWidth,
        height: fullWidthHeight,
      };
    }

    const availableWidth = pageWidth - this.FOOTER_HORIZONTAL_MARGIN * 2;
    const marginScale = availableWidth / originalWidth;
    const marginHeight = originalHeight * marginScale;

    if (marginHeight <= this.FOOTER_MAX_HEIGHT) {
      return {
        x: this.FOOTER_HORIZONTAL_MARGIN,
        width: availableWidth,
        height: marginHeight,
      };
    }

    const heightScale = this.FOOTER_MAX_HEIGHT / originalHeight;
    const scaledWidth = originalWidth * heightScale;
    const x = (pageWidth - scaledWidth) / 2;

    return {
      x,
      width: scaledWidth,
      height: this.FOOTER_MAX_HEIGHT,
    };
  }

  private getImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        resolve({width: image.naturalWidth, height: image.naturalHeight});
        URL.revokeObjectURL(url);
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read image dimensions.'));
      };

      image.src = url;
    });
  }

  public async onHeaderLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      return;
    }

    const error = await this.validateImageFile(file);

    if (error) {
      const control = this.formGroup.get('logoFirstPage');
      control?.setValue(null);
      control?.markAsTouched();
      control?.setErrors({ invalidImage: error });
      input.value = '';
      return;
    }

    this.formGroup.patchValue({logoFirstPage: file});

    if (this.headerPreviewUrl) {
      URL.revokeObjectURL(this.headerPreviewUrl);
    }

    this.headerPreviewUrl = URL.createObjectURL(file);
    await this.buildHeaderPreviewStyle(file);
  }

  public async onFooterLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.formGroup.patchValue({footerImage: file});

    if (this.footerPreviewUrl) {
      URL.revokeObjectURL(this.footerPreviewUrl);
    }

    this.footerPreviewUrl = file ? URL.createObjectURL(file) : null;
    this.footerPreviewStyle = null;

    if (file) {
      await this.buildFooterPreviewStyle(file);
    }
  }

  private async validateImageFile(file: File): Promise<string | null> {
    if (!file.type.startsWith('image/')) {
      return 'Upload een geldig afbeeldingsbestand.';
    }

    if (file.size > this.MAX_IMAGE_BYTES) {
      return 'De afbeelding mag maximaal 2 MB zijn.';
    }

    const size = await this.getImageSize(file);

    if (size.width > this.MAX_IMAGE_WIDTH) {
      return `De afbeelding is te breed. Maximum is ${this.MAX_IMAGE_WIDTH}px.`;
    }

    if (size.height > this.MAX_IMAGE_HEIGHT) {
      return `De afbeelding is te hoog. Maximum is ${this.MAX_IMAGE_HEIGHT}px.`;
    }

    if (size.width * size.height > this.MAX_TOTAL_PIXELS) {
      return 'De afbeelding is te groot om te verwerken.';
    }

    return null;
  }
}
